// Stripe API utilities for frontend

const STRIPE_FUNCTION_URL = '/.netlify/functions/stripe-invoice';

// Create a Stripe customer
export async function createStripeCustomer({ name, email, phone, clientId }) {
  console.log('>>> stripeApi.createStripeCustomer called');
  try {
    console.log('>>> Fetching:', STRIPE_FUNCTION_URL);
    const response = await fetch(STRIPE_FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'createCustomer',
        data: { name, email, phone, metadata: { clientId } }
      })
    });

    console.log('>>> Response status:', response.status);
    const result = await response.json();
    console.log('>>> Response body:', result);
    if (!result.success) throw new Error(result.error || 'Unknown error');
    return result;
  } catch (error) {
    console.error('Error creating Stripe customer:', error);
    throw error;
  }
}

// Create a Stripe invoice
export async function createStripeInvoice({ 
  customerId, 
  lineItems, 
  dueDate, 
  invoiceNumber,
  invoiceId,
  jobId,
  // Deposit fields
  isDepositInvoice,
  depositAmount,
  originalTotal
}) {
  console.log('>>> stripeApi.createStripeInvoice called');
  console.log('>>> customerId:', customerId);
  console.log('>>> lineItems:', lineItems);
  console.log('>>> isDepositInvoice:', isDepositInvoice);
  console.log('>>> depositAmount:', depositAmount);
  try {
    const response = await fetch(STRIPE_FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'createInvoice',
        data: { 
          customerId, 
          lineItems, 
          dueDate, 
          invoiceNumber,
          metadata: { invoiceId, jobId },
          // Pass deposit fields
          isDepositInvoice,
          depositAmount,
          originalTotal
        }
      })
    });

    console.log('>>> createInvoice response status:', response.status);
    const result = await response.json();
    console.log('>>> createInvoice result:', result);
    if (!result.success) throw new Error(result.error || 'Unknown error');
    return result;
  } catch (error) {
    console.error('Error creating Stripe invoice:', error);
    throw error;
  }
}

// Send a Stripe invoice
export async function sendStripeInvoice(stripeInvoiceId) {
  console.log('>>> stripeApi.sendStripeInvoice called:', stripeInvoiceId);
  try {
    const response = await fetch(STRIPE_FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'sendInvoice',
        data: { invoiceId: stripeInvoiceId }
      })
    });

    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error sending Stripe invoice:', error);
    throw error;
  }
}

// Finalize a Stripe invoice (no Stripe email - we send our own with portal link)
export async function finalizeStripeInvoice(stripeInvoiceId) {
  console.log('>>> stripeApi.finalizeStripeInvoice called:', stripeInvoiceId);
  try {
    const response = await fetch(STRIPE_FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'finalizeInvoice',
        data: { invoiceId: stripeInvoiceId }
      })
    });

    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error finalizing Stripe invoice:', error);
    throw error;
  }
}

// Send a Stripe invoice with tip added as line item
export async function sendStripeInvoiceWithTip(stripeInvoiceId, customerId, tipAmountCents) {
  console.log('>>> stripeApi.sendStripeInvoiceWithTip called:', stripeInvoiceId, 'tip:', tipAmountCents);
  try {
    const response = await fetch(STRIPE_FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'sendInvoiceWithTip',
        data: { invoiceId: stripeInvoiceId, customerId, tipAmountCents }
      })
    });

    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error sending Stripe invoice with tip:', error);
    throw error;
  }
}

// Void a Stripe invoice (used before resending)
export async function voidStripeInvoice(stripeInvoiceId) {
  console.log('>>> stripeApi.voidStripeInvoice called:', stripeInvoiceId);
  try {
    const response = await fetch(STRIPE_FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'voidInvoice',
        data: { invoiceId: stripeInvoiceId }
      })
    });
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error voiding Stripe invoice:', error);
    // Non-fatal — continue with resend
    return { success: false, error: error.message };
  }
}

// Get Stripe invoice status
export async function getStripeInvoice(stripeInvoiceId) {
  try {
    const response = await fetch(STRIPE_FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'getInvoice',
        data: { invoiceId: stripeInvoiceId }
      })
    });

    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error getting Stripe invoice:', error);
    throw error;
  }
}

// Find a Stripe invoice by our internal invoice id (matched on Stripe metadata).
// Returns { success, found, invoice? } — `found: false` when no match exists.
export async function findStripeInvoiceByInternalId(internalInvoiceId) {
  try {
    const response = await fetch(STRIPE_FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'findInvoiceByInternalId',
        data: { internalInvoiceId }
      })
    });

    const result = await response.json();
    if (!result.success) throw new Error(result.error);
    return result;
  } catch (error) {
    console.error('Error finding Stripe invoice by internal id:', error);
    throw error;
  }
}

// Full invoice flow: create customer if needed, create invoice, send it
export async function processInvoiceToStripe(invoice, client) {
  let stripeCustomerId = client.stripeCustomerId;

  // Create Stripe customer if doesn't exist
  if (!stripeCustomerId) {
    const customerResult = await createStripeCustomer({
      name: `${client.firstName} ${client.lastName}`,
      email: client.email,
      phone: client.phone,
      clientId: client.id
    });
    stripeCustomerId = customerResult.customerId;
  }

  // Create the Stripe invoice
  const invoiceResult = await createStripeInvoice({
    customerId: stripeCustomerId,
    lineItems: invoice.lineItems,
    dueDate: invoice.dueDate,
    invoiceNumber: invoice.invoiceNumber,
    invoiceId: invoice.id,
    jobId: invoice.jobId
  });

  // Send the invoice
  const sendResult = await sendStripeInvoice(invoiceResult.invoiceId);

  return {
    stripeCustomerId,
    stripeInvoiceId: invoiceResult.invoiceId,
    hostedUrl: sendResult.hostedUrl,
    pdfUrl: sendResult.pdfUrl
  };
}
